import { motion } from 'framer-motion';
import { Play, CheckCircle2 } from 'lucide-react';

interface HeroSectionProps {
  onGetStarted?: () => void;
}

export const HeroSection = ({ onGetStarted }: HeroSectionProps = {}) => {
  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      // Vérifier s'il y a un code referral dans l'URL ou localStorage
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      const referralUsername = sessionStorage.getItem('referralUsername');
      
      // Si on a un code referral, le conserver dans l'URL lors de la navigation
      if (refCode || referralUsername) {
        const url = refCode ? `/register?ref=${encodeURIComponent(refCode)}` : '/register';
        window.location.href = url;
      } else {
        window.location.href = '/register';
      }
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 md:px-8 w-full py-12 sm:py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left: Text Content */}
          <div className="space-y-6 sm:space-y-8">
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            className="text-neutral-300 tracking-wide text-xs sm:text-[11px] md:text-sm font-medium"
            >
            Made for {""}
            <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-400 bg-clip-text text-transparent">
              Freelancers
            </span>{" "}
            by Freelancers
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] sm:leading-[1.05] tracking-[-0.02em] sm:tracking-[-0.04em]"
            >
              {['Streamline Your Workflow.', 'Elevate Your Business.'].map((line, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className={index === 1 ? "block bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-300 bg-clip-text text-transparent" : "block text-white"}
                >
                  {line}
                </motion.span>
              ))}
            </motion.h1>

            {/* Subtext */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="space-y-3 sm:space-y-4"
            >
              <p className="text-base sm:text-lg text-neutral-300 max-w-lg">
                FiverFlow helps you organize clients, track orders, and grow your freelance business across every platform. Join freelancers who have already transformed how they work.
              </p>
              <p className="text-sm sm:text-base text-neutral-400">
                Say goodbye to chaos — focus on delivery, not admin.
              </p>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <motion.button
                onClick={handleGetStarted}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center rounded-full text-white font-medium text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-400 shadow-[0_20px_80px_rgba(99,102,241,0.6)] hover:shadow-[0_30px_120px_rgba(99,102,241,0.9)] transition-shadow min-h-[44px] w-full sm:w-auto"
              >
                Try FiverFlow for free
              </motion.button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-neutral-400"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-400" />
                <span>Fast onboarding</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-400" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-400" />
                <span>Free trial</span>
              </div>
            </motion.div>
          </div>

          {/* Right: Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative mt-8 md:mt-0"
          >
            <motion.div
              className="rounded-xl border border-white/10 bg-[rgba(255,255,255,0.03)] backdrop-blur-xl shadow-[0_30px_120px_rgba(0,0,0,0.8)] p-4 sm:p-6 md:p-8 min-h-[300px] sm:min-h-[350px] md:min-h-[400px]"
              whileHover={{ rotateX: 5, rotateY: 5 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <div className="text-neutral-400 text-sm font-mono space-y-4">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" />
                  <span className="ml-4 text-xs text-neutral-500">preview</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-full" />
                      <div>
                        <div className="text-white">Active Tasks</div>
                        <div className="text-xs text-neutral-500">12 in progress</div>
                      </div>
                    </div>
                    <div className="text-indigo-400">→</div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-full" />
                      <div>
                        <div className="text-white">Client Management</div>
                        <div className="text-xs text-neutral-500">45 clients</div>
                      </div>
                    </div>
                    <div className="text-purple-400">→</div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-cyan-500/20 rounded-full" />
                      <div>
                        <div className="text-white">Revenue This Month</div>
                        <div className="text-xs text-neutral-500">$12,450</div>
                      </div>
                    </div>
                    <div className="text-cyan-400">↑ 32%</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

